<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { FormBuilderField } from '@/types/form-builder-types'
import type { VueFormConfig } from '@/types/wizard-types'
import { FIELD_TYPE_ICONS, FIELD_TYPE_LABELS } from '@/utils/field-icons'
import { convertFieldsToSchema, convertSchemaToFields } from '@/utils/vueform-helpers'
import FieldConfigModal from './FieldConfigModal.vue'

const props = defineProps<{
  modelValue?: VueFormConfig
}>()

const emit = defineEmits<{
  'update:modelValue': [config: VueFormConfig]
}>()

// State
const fields = ref<FormBuilderField[]>([])
const showModal = ref(false)

// Hydrate fields from the incoming schema when the prop is first set.
// The guard prevents clobbering in-session edits (only loads when fields is empty).
watch(
  () => props.modelValue?.schema,
  (schema) => {
    if (schema && fields.value.length === 0) {
      fields.value = convertSchemaToFields(schema)
    }
  },
  { immediate: true },
)
const editingField = ref<FormBuilderField | undefined>(undefined)
const showDeleteModal = ref(false)
const fieldToDelete = ref<FormBuilderField | null>(null)

// Computed
const existingFieldNames = computed(() => fields.value.map((f) => f.name))

const fieldCount = computed(() => fields.value.length)

// Methods
function addField() {
  editingField.value = undefined
  showModal.value = true
}

function editField(field: FormBuilderField) {
  editingField.value = field
  showModal.value = true
}

function saveField(field: FormBuilderField) {
  const index = fields.value.findIndex((f) => f.id === field.id)

  if (index >= 0) {
    // Update existing
    fields.value[index] = field
  } else {
    // Add new
    field.order = fields.value.length
    fields.value.push(field)
  }

  updateSchema()
}

function duplicateField(field: FormBuilderField) {
  const newField = JSON.parse(JSON.stringify(field)) as FormBuilderField
  newField.id = `field_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
  newField.name = `${field.name}_copy`
  newField.label = `${field.label} (Copy)`
  newField.order = fields.value.length
  fields.value.push(newField)
  updateSchema()
}

function deleteField(field: FormBuilderField) {
  fieldToDelete.value = field
  showDeleteModal.value = true
}

function confirmDelete() {
  if (!fieldToDelete.value) return
  const index = fields.value.findIndex((f) => f.id === fieldToDelete.value!.id)
  if (index >= 0) {
    fields.value.splice(index, 1)
    // Re-order
    fields.value.forEach((f, i) => (f.order = i))
    updateSchema()
  }
  showDeleteModal.value = false
  fieldToDelete.value = null
}

// --- Drag & Drop for fields list ---
const draggedFieldIndex = ref<number | null>(null)

function onFieldDragStart(e: DragEvent, index: number) {
  draggedFieldIndex.value = index
  e.dataTransfer?.setData('text/plain', String(index))
  e.dataTransfer!.effectAllowed = 'move'
}

function onFieldDragOver(e: DragEvent) {
  e.preventDefault()
  e.dataTransfer!.dropEffect = 'move'
}

function onFieldDrop(e: DragEvent, dropIndex: number) {
  e.preventDefault()
  const fromIndex = draggedFieldIndex.value
  if (fromIndex === null || fromIndex === dropIndex) {
    draggedFieldIndex.value = null
    return
  }

  const [moved] = fields.value.splice(fromIndex, 1)
  fields.value.splice(dropIndex, 0, moved)

  // Re-order
  fields.value.forEach((f, i) => (f.order = i))
  draggedFieldIndex.value = null
  updateSchema()
}

function onFieldDragEnd() {
  draggedFieldIndex.value = null
}

function updateSchema() {
  const schema = convertFieldsToSchema(fields.value)
  emit('update:modelValue', {
    schema,
    displayErrors: true,
  })
}

function getFieldIcon(field: FormBuilderField): string {
  return FIELD_TYPE_ICONS[field.type] || 'bi-question-circle'
}

function getFieldTypeLabel(field: FormBuilderField): string {
  return FIELD_TYPE_LABELS[field.type] || field.type
}

function getValidationSummary(field: FormBuilderField): string {
  const parts: string[] = []

  if (field.validation?.required) parts.push('required')
  if (field.validation?.email) parts.push('email')
  if (field.validation?.url) parts.push('url')
  if (field.validation?.minLength) parts.push(`min: ${field.validation.minLength}`)
  if (field.validation?.maxLength) parts.push(`max: ${field.validation.maxLength}`)
  if (field.validation?.pattern) parts.push('regex')

  return parts.length > 0 ? parts.join(', ') : 'none'
}
</script>

<template>
  <div class="form-builder-editor">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-3 flex-shrink-0">
      <h3 class="mb-0">
        <i class="bi bi-ui-checks me-2"></i>
        Form Fields
        <span v-if="fieldCount > 0" class="badge bg-primary ms-2">{{ fieldCount }}</span>
      </h3>
      <button type="button" class="bb-btn-primary-cta btn-sm" @click="addField">
        <i class="bi bi-plus-circle me-1"></i> Add Field
      </button>
    </div>

    <!-- Empty State -->
    <div v-if="fields.length === 0" class="empty-state flex-shrink-0 rounded bg-body-tertiary">
      <div class="text-center py-5">
        <i class="bi bi-ui-checks display-4 text-muted"></i>
        <p class="mt-3 text-muted fw-semibold">No fields added yet</p>
        <p class="text-muted">Click "Add Field" to start building your form</p>
        <button type="button" class="bb-btn-primary-cta" @click="addField">
          <i class="bi bi-plus-circle me-1"></i> Add Your First Field
        </button>
      </div>
    </div>

    <!-- Field List -->
    <div v-else class="field-list">
      <div
        v-for="(field, index) in fields"
        :key="field.id"
        class="field-item card mb-2"
        :class="{ 'field-dragging': draggedFieldIndex === index }"
        draggable="true"
        @dragstart="onFieldDragStart($event, index)"
        @dragend="onFieldDragEnd"
        @dragover="onFieldDragOver"
        @drop="onFieldDrop($event, index)"
      >
        <div class="card-body p-3 ps-2">
          <!-- Top row: drag handle, label, type badge floated right -->
          <div class="d-flex align-items-center gap-2 mb-1">
            <div class="drag-handle text-muted">
              <i class="bi bi-grip-vertical"></i>
            </div>
            <i :class="getFieldIcon(field)" class="text-primary"></i>
            <strong class="flex-grow-1 min-w-0 text-truncate">{{ field.label }}</strong>
            <span v-if="field.required" class="badge bg-danger badge-sm">Required</span>
            <span class="badge bg-secondary badge-sm">{{ getFieldTypeLabel(field) }}</span>
          </div>

          <!-- Details row -->
          <div class="small text-muted ms-4">
            <span class="me-3">
              <i class="bi bi-code-slash me-1"></i>{{ field.name }}
            </span>
            <span v-if="field.placeholder" class="me-3">
              <i class="bi bi-chat-quote me-1"></i>{{ field.placeholder }}
            </span>
            <span v-if="field.validation" class="me-3">
              <i class="bi bi-shield-check me-1"></i>{{ getValidationSummary(field) }}
            </span>
            <span v-if="field.options && field.options.length > 0">
              <i class="bi bi-list-ul me-1"></i>{{ field.options.length }} option(s)
            </span>
          </div>

          <!-- Actions row: single line of buttons -->
          <div class="d-flex justify-content-end gap-1 mt-2">
            <button
              type="button"
              class="bb-btn-secondary-cta btn-sm"
              @click="editField(field)"
            >
              <i class="bi bi-pencil"></i> Edit
            </button>
            <button
              type="button"
              class="bb-btn-secondary-cta btn-sm"
              @click="duplicateField(field)"
            >
              <i class="bi bi-files"></i> Duplicate
            </button>
            <button
              type="button"
              class="bb-btn-danger btn-sm"
              @click="deleteField(field)"
            >
              <i class="bi bi-trash"></i> Delete
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Field Config Modal -->
    <FieldConfigModal
      :show="showModal"
      :field="editingField"
      :existing-field-names="existingFieldNames"
      @close="showModal = false"
      @save="saveField"
    />

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="modal fade show"
      style="display: block"
      @click.self="showDeleteModal = false"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title text-danger">
              <i class="bi bi-exclamation-triangle me-2"></i>Delete Field
            </h2>
            <button type="button" class="btn-close" @click="showDeleteModal = false"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete <strong>{{ fieldToDelete?.label }}</strong>?</p>
            <p class="text-muted mb-0">This action cannot be undone.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="bb-btn-cancel" @click="showDeleteModal = false">Cancel</button>
            <button type="button" class="btn btn-danger" @click="confirmDelete">Delete</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showDeleteModal" class="modal-backdrop fade show" @click="showDeleteModal = false"></div>
  </div>
</template>

<style scoped lang="scss">
.form-builder-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.field-list {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.empty-state {
  border: 2px dashed var(--bs-border-color);
  min-height: 200px;
}

.field-item {
  transition: all 0.2s;
}

.field-item:hover {
  border-left: 3px solid var(--bs-primary);
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}


.drag-handle {
  cursor: grab;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.drag-handle:active {
  cursor: grabbing;
}

.field-dragging {
  opacity: 0.4;
}

.badge-sm {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
}
</style>
